import axiosClient from "../lib/axiosClient";
import { uploadFilesBurstConcurrent } from "../hooks/useReportUpload";

/**
 * Create the report row on the server (no attachment uploads).
 * Used by the offline queue to persist serverReportId before streaming uploads.
 */
export async function createTechnicianReport({
  backendUrl,
  token,
  fields,
  hasAttachments,
}) {
  const {
    date,
    sr,
    address,
    appointmentType,
    routerOption,
    routerSerial,
    ontOption,
    ontSerial,
    spiralOption,
    spiralMeters,
    inesLength,
    prizakia,
    notes,
  } = fields;

  const payload = {
    sr,
    date,
    address,
    appointmentType,
    routerOption,
    routerSerial: routerOption === "yes" ? routerSerial : null,
    ontOption,
    ontSerial: ontOption === "yes" ? ontSerial : null,
    spiralOption,
    spiralMeters: spiralOption === "yes" ? spiralMeters : null,
    inesLength,
    prizakia,
    notes,
  };

  if (hasAttachments) {
    let reportResponse;
    try {
      reportResponse = await axiosClient.post(
        `${backendUrl}/api/reports/create`,
        payload
      );
      return reportResponse.data.id;
    } catch (error1) {
      if (error1.response?.status === 404) {
        try {
          reportResponse = await axiosClient.post(`${backendUrl}/api/reports`, payload);
          return reportResponse.data.id;
        } catch (error2) {
          if (error2.response?.status === 404) {
            const formData = new FormData();
            formData.append("sr", sr);
            formData.append("date", date);
            formData.append("address", address);
            formData.append("appointmentType", appointmentType);
            formData.append("routerOption", routerOption);
            if (routerOption === "yes") {
              formData.append("routerSerial", routerSerial);
            }
            formData.append("ontOption", ontOption);
            if (ontOption === "yes") {
              formData.append("ontSerial", ontSerial);
            }
            formData.append("spiralOption", spiralOption);
            if (spiralOption === "yes") {
              formData.append("spiralMeters", spiralMeters);
            }
            formData.append("inesLength", inesLength);
            formData.append("prizakia", prizakia);
            formData.append("notes", notes);
            formData.append("attachments", "");
            reportResponse = await axiosClient.post(`${backendUrl}/submit-report`, formData);
            return reportResponse.data.id;
          }
          throw error2;
        }
      }
      throw error1;
    }
  }

  const formData = new FormData();
  formData.append("sr", sr);
  formData.append("date", date);
  formData.append("address", address);
  formData.append("appointmentType", appointmentType);
  formData.append("routerOption", routerOption);
  if (routerOption === "yes") {
    formData.append("routerSerial", routerSerial);
  }
  formData.append("ontOption", ontOption);
  if (ontOption === "yes") {
    formData.append("ontSerial", ontSerial);
  }
  formData.append("spiralOption", spiralOption);
  if (spiralOption === "yes") {
    formData.append("spiralMeters", spiralMeters);
  }
  formData.append("inesLength", inesLength);
  formData.append("prizakia", prizakia);
  formData.append("notes", notes);

  const reportResponse = await axiosClient.post(`${backendUrl}/submit-report`, formData);
  return reportResponse.data.id;
}

/**
 * Create a technician report on the server and upload attachments (same endpoints as App.handleSubmit).
 * Does not update React state or show toasts — caller handles UX.
 *
 * @param {object} [opts]
 * @param {string|number|null} [opts.existingReportId] — if set, skips report creation and only uploads files (queue resume).
 */
export async function submitTechnicianReport({
  backendUrl,
  token,
  fields,
  files,
  uploadFileAsync,
  onAfterUpload,
  existingReportId = null,
}) {
  const fileList = Array.isArray(files) ? files : [];
  let reportId =
    existingReportId !== undefined && existingReportId !== null && existingReportId !== ""
      ? existingReportId
      : null;

  if (!reportId) {
    reportId = await createTechnicianReport({
      backendUrl,
      token,
      fields,
      hasAttachments: fileList.length > 0,
    });
  }

  if (!reportId) {
    throw new Error("Δεν λήφθηκε αναγνωριστικό αναφοράς");
  }

  if (fileList.length > 0) {
    await uploadFilesBurstConcurrent(uploadFileAsync, fileList, reportId);
  }

  if (typeof onAfterUpload === "function") {
    Promise.resolve(onAfterUpload(reportId)).catch((err) => {
      console.error("Post-upload hook (non-critical):", err);
    });
  }

  return { reportId };
}
